import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const KES_FORMAT = new Intl.NumberFormat("en-KE", {
  style: "currency",
  currency: "KES",
  maximumFractionDigits: 2,
});

const KES_FORMAT_WHOLE = new Intl.NumberFormat("en-KE", {
  style: "currency",
  currency: "KES",
  maximumFractionDigits: 0,
});

const NUMBER_FORMAT = new Intl.NumberFormat("en-KE");

/**
 * Format a KES money figure with thousands separators, e.g. `KSh 1,234,567.00`.
 * Pass `{ whole: true }` to drop the decimals (e.g. stat cards / large totals).
 * Returns an em dash for null/undefined/NaN so callers don't render "KSh NaN".
 */
export function formatKes(
  value: number | null | undefined,
  options?: { whole?: boolean },
): string {
  if (value == null || Number.isNaN(value)) return "—";
  return (options?.whole ? KES_FORMAT_WHOLE : KES_FORMAT).format(value);
}

/**
 * Format a plain number with thousands separators, e.g. `1,234,567`.
 * Returns an em dash for null/undefined/NaN.
 */
export function formatNumber(
  value: number | null | undefined,
  options?: Intl.NumberFormatOptions,
): string {
  if (value == null || Number.isNaN(value)) return "—";
  return (options ? new Intl.NumberFormat("en-KE", options) : NUMBER_FORMAT).format(value);
}

/**
 * Helper function to handle API errors
 */
export const handleApiError = (error: unknown): never => {
  if (error instanceof Error) {
    throw new Error(`API Error: ${error.message}`);
  }
  throw new Error("An unknown error occurred");
};

type OptimisticOperation = "create" | "update" | "delete" | undefined;
export const getRowClassName = ({
  isSelected,
  optimisticOperation,
}: {
  isSelected: boolean;
  optimisticOperation?: OptimisticOperation;
}) => {
  return cn(
    "transition-colors duration-1000 hover:bg-neutral-100",
    isSelected && "bg-muted",
    optimisticOperation && "animate-highlight",
    optimisticOperation === "create" && "from-green-300",
    optimisticOperation === "update" && "from-blue-300",
    optimisticOperation === "delete" && "from-red-300",
  );
};
